/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { importProvidersFrom, ApplicationConfig, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { provideNoopAnimations, provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthGuard, NoopRedirectAuthService, RedirectAuthService, provideAppConfig, provideCoreAuth, provideI18N } from '@alfresco/adf-core';
import { AppService, provideContentAppExtensions, showBuddhistEraInAdfCards } from '@alfresco/aca-shared';
import { provideApplicationExtensions } from './extensions.module';
import { environment } from '../environments/environment';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import localeJa from '@angular/common/locales/ja';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeNb from '@angular/common/locales/nb';
import localeRu from '@angular/common/locales/ru';
import localeCh from '@angular/common/locales/zh';
import localeAr from '@angular/common/locales/ar';
import localeCs from '@angular/common/locales/cs';
import localePl from '@angular/common/locales/pl';
import localeFi from '@angular/common/locales/fi';
import localeDa from '@angular/common/locales/da';
import localeSv from '@angular/common/locales/sv';
import localeTh from '@angular/common/locales/th';
import { provideRouter, withHashLocation } from '@angular/router';
import { CONTENT_LAYOUT_ROUTES, ContentServiceExtensionModule } from '@alfresco/aca-content';
import { SHELL_APP_SERVICE, SHELL_AUTH_TOKEN, provideShellRoutes } from '@alfresco/adf-core/shell';
import { APP_ROUTES } from './app.routes';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { providePdfViewer } from '@alfresco/adf-core/viewer/pdf';
import { WorkflowBuilderComponent } from './components/workflow/workflow-builder.component';
import { WorkflowWizardComponent } from './components/workflow/wizard/workflow-wizard.component';
import { MyWorkComponent } from './components/workflow/my-work.component';
import { TaskDetailComponent } from './components/workflow/task-detail.component';
import { InstanceDetailComponent } from './components/workflow/instance-detail.component';
import { workflowAdminGuard } from './components/workflow/workflow-admin.guard';
import { migrationAdminGuard } from './components/migration/migration-admin.guard';
import { workflowEnabledGuard } from './components/workflow/workflow-enabled.guard';
import { menuAllowedGuard } from './components/menu-permissions/menu-permission.guard';
import { FilePreviewPageComponent } from './components/preview/file-preview-page.component';
import { AuditLogComponent } from './components/admin/audit-log.component';
import { DocumentExpiryComponent } from './components/expiry/document-expiry.component';
import { EmailTemplatesComponent } from './components/expiry/email-templates.component';
import { DocumentUploadComponent } from './components/upload/document-upload.component';
import { AllDocumentsComponent } from './components/documents/all-documents.component';
import { FavoritesPageComponent } from './components/favorites/favorites-page.component';
import { TrashPageComponent } from './components/trash/trash-page.component';
import { ShareLinksPageComponent } from './components/share-links/share-links-page.component';
import { InsightsPageComponent } from './components/insights/insights-page.component';
import { FormsPageComponent } from './components/forms/forms-page.component';
import { FormBuilderComponent } from './components/forms/form-builder.component';
import { FormSubmissionsComponent } from './components/forms/form-submissions.component';
import { FormFillComponent } from './components/forms/form-fill.component';
import { formsAdminGuard } from './components/forms/forms-admin.guard';
import { PdpaReportComponent } from './components/pdpa/pdpa-report.component';
import { DocumentSearchComponent } from './components/search/document-search.component';
import { CopyMoveComponent } from './components/copy-move/copy-move.component';
import { UsersPageComponent } from './components/users/users-page.component';
import { GroupsPageComponent } from './components/groups/groups-page.component';
import { SettingsComponent } from './components/settings/settings.component';
import { reloadOnLogout } from './components/login/reload-on-logout';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

// Register the custom Dashboard + Workflows pages as children of the shell layout
// at bootstrap (prepended before the shell's wildcard route) so they are available
// the moment login redirects — and they share the single shell instance, so the
// sidenav/header don't flash when navigating to/from them.
const CONTENT_LAYOUT_ROUTES_WITH_DASHBOARD = CONTENT_LAYOUT_ROUTES.map((route) => ({
  ...route,
  // Menu visibility policies — a menu closed to someone hides its entry AND
  // refuses its route, for the routes this app declares and the library's alike.
  canActivateChild: [...(route.canActivateChild ?? []), menuAllowedGuard],
  children: [
    { path: 'dashboard', component: DashboardComponent, data: { title: 'Dashboard' } },
    // My Work is the single inbox: the old task list and the old approvals
    // screen both redirect into it (with ?type=approval preserving links).
    { path: 'my-work', component: MyWorkComponent, canActivate: [workflowEnabledGuard], data: { title: 'My Work' } },
    { path: 'my-work/task/:id', component: TaskDetailComponent, canActivate: [workflowEnabledGuard], data: { title: 'Task' } },
    { path: 'workflows/start', component: WorkflowWizardComponent, canActivate: [workflowEnabledGuard], data: { title: 'Start workflow' } },
    // tracking lands on the same inbox, filtered to running processes
    { path: 'workflows/track', component: MyWorkComponent, canActivate: [workflowEnabledGuard], data: { title: 'Track workflow', track: true } },
    {
      path: 'workflows/instances/:id',
      component: InstanceDetailComponent,
      canActivate: [workflowEnabledGuard],
      data: { title: 'Workflow tracking' }
    },
    // the catalogue moved inside settings — the old address forwards to the
    // section deep link so saved links and menu policies keep working
    { path: 'workflows/manage', redirectTo: 'settings/workflows', pathMatch: 'full' as const },
    { path: 'my-tasks', redirectTo: 'my-work', pathMatch: 'full' as const },
    { path: 'workflow', redirectTo: 'my-work?type=approval', pathMatch: 'full' as const },
    { path: 'approvals', redirectTo: 'my-work?type=approval', pathMatch: 'full' as const },
    { path: 'start-workflow', redirectTo: 'workflows/start', pathMatch: 'full' as const },
    {
      path: 'build-workflow',
      component: WorkflowBuilderComponent,
      canActivate: [workflowEnabledGuard, workflowAdminGuard],
      data: { title: 'Workflow Builder' }
    },
    { path: 'admin-users', component: UsersPageComponent, canActivate: [workflowAdminGuard], data: { title: 'Users' } },
    { path: 'admin-groups', component: GroupsPageComponent, canActivate: [workflowAdminGuard], data: { title: 'Groups' } },
    { path: 'admin-audit', component: AuditLogComponent, canActivate: [workflowAdminGuard], data: { title: 'Audit Log' } },
    { path: 'admin-pdpa', component: PdpaReportComponent, canActivate: [workflowAdminGuard], data: { title: 'PDPA report' } },
    { path: 'document-expiry', component: DocumentExpiryComponent, canActivate: [workflowAdminGuard], data: { title: 'Document Expiry' } },
    { path: 'email-templates', component: EmailTemplatesComponent, canActivate: [workflowAdminGuard], data: { title: 'Email Templates' } },
    { path: 'upload', component: DocumentUploadComponent, data: { title: 'Upload Document' } },
    // OCR used to have a page of its own; it is part of uploading now
    { path: 'ocr-upload', redirectTo: 'upload', pathMatch: 'full' as const },
    { path: 'all-documents', component: AllDocumentsComponent, data: { title: 'All Documents' } },
    { path: 'my-favorites', component: FavoritesPageComponent, data: { title: 'Favorites' } },
    { path: 'my-trash', component: TrashPageComponent, data: { title: 'Trash' } },
    { path: 'share-links', component: ShareLinksPageComponent, data: { title: 'Share links' } },
    { path: 'insights', component: InsightsPageComponent, data: { title: 'Organisation overview' } },
    { path: 'forms', component: FormsPageComponent, data: { title: 'Forms' } },
    { path: 'forms/new', component: FormBuilderComponent, canActivate: [formsAdminGuard], data: { title: 'Form builder' } },
    { path: 'forms/:id/edit', component: FormBuilderComponent, canActivate: [formsAdminGuard], data: { title: 'Form builder' } },
    { path: 'forms/:id/submissions', component: FormSubmissionsComponent, canActivate: [formsAdminGuard], data: { title: 'Form submissions' } },
    { path: 'forms/:id', component: FormFillComponent, data: { title: 'Fill in a form' } },
    { path: 'document-search', component: DocumentSearchComponent, data: { title: 'Search' } },
    { path: 'copy-move', component: CopyMoveComponent, data: { title: 'Copy or move' } },
    {
      path: 'settings/data-migration/:tab',
      component: SettingsComponent,
      canActivate: [migrationAdminGuard],
      data: { title: 'Data Migration', section: 'migration' }
    },
    {
      path: 'settings/data-migration',
      component: SettingsComponent,
      canActivate: [migrationAdminGuard],
      data: { title: 'Data Migration', section: 'migration' }
    },
    {
      path: 'settings/workflows',
      component: SettingsComponent,
      canActivate: [workflowAdminGuard],
      data: { title: 'Workflow', section: 'workflow' }
    },
    { path: 'settings', component: SettingsComponent, data: { title: 'Settings' } },
    { path: 'document/:id', component: FilePreviewPageComponent, data: { title: 'Preview' } },
    ...(route.children ?? [])
  ]
}));

registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeEs);
registerLocaleData(localeJa);
registerLocaleData(localeNl);
registerLocaleData(localePt);
registerLocaleData(localeNb);
registerLocaleData(localeRu);
registerLocaleData(localeCh);
registerLocaleData(localeAr);
registerLocaleData(localeCs);
registerLocaleData(localePl);
registerLocaleData(localeFi);
registerLocaleData(localeDa);
registerLocaleData(localeSv);
registerLocaleData(localeTh);

/**
 * Angular's `DatePipe` reads `LOCALE_ID` from DI, not from `app.config.json`.
 * Without this provider every `| date` in the application renders US English
 * month names while ADF's own components render Thai — see UI-0012.
 */
const APP_LOCALE = { provide: LOCALE_ID, useValue: 'th' };

/** `ThaiDatePipe` formats through `DatePipe`, so it must be injectable. */
const APP_DATE_PIPE = DatePipe;

export const AppConfig: ApplicationConfig = {
  providers: [
    APP_LOCALE,
    APP_DATE_PIPE,
    // ADF's metadata card formats its dates itself, out of reach of `acaDate`
    provideAppInitializer(showBuddhistEraInAdfCards),
    // nothing of the previous person's session survives signing out
    provideAppInitializer(() => reloadOnLogout()),
    // Without this the ADF viewer renders nothing for a PDF and logs
    // "PDF viewer is not configured" — it ships the renderer as a separate
    // entry point that the application has to opt into.
    providePdfViewer(),
    provideCoreAuth({ useHash: true }),
    // This app authenticates with BASIC auth (see app.config.json `authType`),
    // which does not use OIDC. Override the OIDC redirect service with a no-op so
    // ADF's auth initializer does not eagerly load the OpenID discovery document
    // — that request 404s at /auth/realms/... when no Keycloak/IdP is running and
    // floods the console on every startup. The `oauth2` block is kept in
    // app.config.json so SSO can be turned on later: set `authType` to "OAUTH"
    // and remove this override to restore the real RedirectAuthService.
    { provide: RedirectAuthService, useClass: NoopRedirectAuthService },
    provideAppConfig(),
    importProvidersFrom(ContentServiceExtensionModule),
    provideI18N({
      assets: [['app', 'assets']]
    }),
    provideContentAppExtensions(),
    provideApplicationExtensions(),
    provideRouter(APP_ROUTES, withHashLocation()),
    provideHttpClient(withInterceptorsFromDi()),
    environment.e2e ? provideNoopAnimations() : provideAnimations(),
    provideShellRoutes(CONTENT_LAYOUT_ROUTES_WITH_DASHBOARD),
    {
      provide: SHELL_APP_SERVICE,
      useClass: AppService
    },
    {
      provide: SHELL_AUTH_TOKEN,
      useValue: AuthGuard
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 10000,
        politeness: 'polite'
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'always', subscriptSizing: 'dynamic' }
    }
  ]
};
